import React, { useState, useEffect, useRef, useCallback } from 'react';
import { PendulumState, createPendulum, applyImpulse, stepPendulum } from './physics';

export interface PaperScrapProps {
  char: string;
  wordIndex: number;
  charIndex: number;
  letterIndex: number;
  initialRotation?: number;
  onTriggerInfo: () => void;
  onTriggerX: () => void;
}

/**
 * All local paper letter PNGs located in /Images/Letters on Paper/
 * Supporting every letter in "iNTERACTIVE ART EXHIBITION" + schzstrg letters.
 */
export const LOCAL_LETTER_PAPERS: Record<string, string> = {
  a: '/Images/Letters on Paper/letter_A.png',
  b: '/Images/Letters on Paper/letter_B.png',
  c: '/Images/Letters on Paper/paper_c.png',
  e: '/Images/Letters on Paper/letter_E.png',
  g: '/Images/Letters on Paper/paper_g.png',
  h: '/Images/Letters on Paper/paper_h.png',
  i: '/Images/Letters on Paper/letter_i.png',
  n: '/Images/Letters on Paper/letter_N.png',
  o: '/Images/Letters on Paper/letter_O.png',
  r: '/Images/Letters on Paper/paper_r.png',
  s: '/Images/Letters on Paper/paper_s.png',
  t: '/Images/Letters on Paper/paper_t.png',
  v: '/Images/Letters on Paper/letter_V.png',
  x: '/Images/Letters on Paper/letter_X.png',
  z: '/Images/Letters on Paper/paper_z.png'
};

const CARDSTOCK_BG = 'https://ik.imagekit.io/schzstrg/tr:w-600,q-80/cardstock.png';
const SCRAP_FALLBACK = 'https://placehold.co/120x160/png?text=paper+scrap';

export const PaperScrap: React.FC<PaperScrapProps> = ({
  char,
  initialRotation = 0,
  onTriggerInfo,
  onTriggerX
}) => {
  const lowerChar = char.toLowerCase();
  const localPaperUrl = LOCAL_LETTER_PAPERS[lowerChar] || null;

  const [paperImgSrc, setPaperImgSrc] = useState<string | null>(localPaperUrl);
  const [bgImage, setBgImage] = useState<string>(CARDSTOCK_BG);
  const [pendulum, setPendulum] = useState<PendulumState>(() => createPendulum(initialRotation));
  const [isSwinging, setIsSwinging] = useState(false);
  const [isVoidCollapsed, setIsVoidCollapsed] = useState(false);
  const isVoidCollapsedRef = useRef(false);
  const animFrameRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number>(performance.now());
  const containerRef = useRef<HTMLDivElement | null>(null);

  const isSwingingRef = useRef(false);
  const isPointerDownRef = useRef(false);
  const lastWindTimeRef = useRef<number>(0);

  // Stop all pendulum swinging and cancel loops immediately when void collapse starts
  useEffect(() => {
    const handleCollapse = () => {
      isVoidCollapsedRef.current = true;
      setIsVoidCollapsed(true);
      isSwingingRef.current = false;
      setIsSwinging(false);
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
        animFrameRef.current = null;
      }
    };

    window.addEventListener('schz-void-collapse', handleCollapse);
    if ((window as any).__SCHZ_COLLAPSED__) {
      handleCollapse();
    }
    return () => {
      window.removeEventListener('schz-void-collapse', handleCollapse);
    };
  }, []);

  // Update image source if char changes
  useEffect(() => {
    setPaperImgSrc(LOCAL_LETTER_PAPERS[lowerChar] || null);
  }, [lowerChar]);

  // Physics animation loop for paper swing
  useEffect(() => {
    if (!isSwinging || isVoidCollapsedRef.current || (window as any).__SCHZ_COLLAPSED__) return;
    isSwingingRef.current = true;

    const animate = (time: number) => {
      if (isVoidCollapsedRef.current || (window as any).__SCHZ_COLLAPSED__) {
        isSwingingRef.current = false;
        setIsSwinging(false);
        return;
      }

      const dt = (time - lastTimeRef.current) / 1000;
      lastTimeRef.current = time;

      setPendulum((prev) => {
        const next = stepPendulum(prev, dt);
        if (Math.abs(next.velocity) < 0.05 && Math.abs(next.angle - next.targetAngle) < 0.04) {
          isSwingingRef.current = false;
          setIsSwinging(false);
          return { ...next, angle: next.targetAngle, velocity: 0 };
        }
        return next;
      });

      if (isSwingingRef.current && !isVoidCollapsedRef.current && !(window as any).__SCHZ_COLLAPSED__) {
        animFrameRef.current = requestAnimationFrame(animate);
      }
    };

    lastTimeRef.current = performance.now();
    animFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [isSwinging]);

  const triggerImpulse = useCallback((strength = 20, customDamping = 0.968, customDir?: number) => {
    if (isVoidCollapsedRef.current || (window as any).__SCHZ_COLLAPSED__) return;
    const dir = customDir !== undefined ? customDir : (Math.random() > 0.5 ? 1 : -1);
    setPendulum((prev) => applyImpulse(prev, dir * strength, customDamping));
    if (!isSwingingRef.current) {
      lastTimeRef.current = performance.now();
      setIsSwinging(true);
    }
  }, []);

  // Listen to aerodynamic wind wake events triggered by cursor/finger movement
  useEffect(() => {
    const handleWind = (e: Event) => {
      if (isVoidCollapsedRef.current || (window as any).__SCHZ_COLLAPSED__) return;
      const { x, y, vx, speed } = (e as CustomEvent).detail;
      const el = containerRef.current;
      if (!el) return;

      // Minimum delta speed required to trigger wind sway (ignoring slow drifts/hovering)
      const MIN_DELTA_SPEED = 300; // px/s
      if (speed < MIN_DELTA_SPEED) return;

      const now = performance.now();
      // Cooldown between impulses to prevent rapid-fire jerky stacking
      if (now - lastWindTimeRef.current < 160) return;

      // Prevent jerking while user is actively pressing down to click
      if (isPointerDownRef.current) return;

      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width * 0.5;
      const cy = rect.top + rect.height * 0.5;
      const dx = x - cx;
      const dy = y - cy;
      const dist = Math.hypot(dx, dy);

      const maxRadius = 120;
      if (dist < maxRadius) {
        // If cursor is directly targeting this paper letter, dampen wind so it doesn't dodge under the click
        const isDirectlyOver =
          x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
        const targetingFactor = isDirectlyOver ? 0.35 : 1.0;

        // Wind direction: follow swipe direction vx, or deflect away from cursor if vertical
        const dir = Math.abs(vx) >= 15 ? (vx > 0 ? 1 : -1) : (dx >= 0 ? 1 : -1);

        // Non-linear aerodynamic proximity falloff
        const normDist = dist / maxRadius;
        const influence = Math.pow(Math.max(0, 1 - normDist), 1.35) * targetingFactor;

        // Base sway cut in half (from 24 to 12)
        const baseX = 12;
        const speedDelta = speed - MIN_DELTA_SPEED;
        // Factor cut down to 80% (from 0.096 to 0.0768, cap from 260 to 208)
        const speedFactor = Math.min(speedDelta * 0.0768, 208);
        const swayAmount = baseX + speedFactor;

        // Dynamic damping based on swipe speed
        const dynamicDamping = Math.min(0.968 + (speedDelta / 3000) * 0.008, 0.976);
        const impulse = influence * swayAmount * dir;

        lastWindTimeRef.current = now;
        setPendulum((prev) => applyImpulse(prev, impulse, dynamicDamping));
        if (!isSwingingRef.current) {
          lastTimeRef.current = performance.now();
          setIsSwinging(true);
        }
      }
    };

    window.addEventListener('schz-paper-wind', handleWind, { passive: true });
    return () => {
      window.removeEventListener('schz-paper-wind', handleWind);
    };
  }, []);

  const isInteractive = lowerChar === 'i' || lowerChar === 'x';
  const isCollapsed = isVoidCollapsed || Boolean((window as any).__SCHZ_COLLAPSED__);

  const handleClick = (e: React.MouseEvent) => {
    if (!isInteractive || isVoidCollapsedRef.current || (window as any).__SCHZ_COLLAPSED__) return;
    e.stopPropagation();

    // Only 'i' and 'X' have special active functionalities
    if (lowerChar === 'i') {
      // Gentle tactile feedback on click instead of a violent snap
      triggerImpulse(18, 0.968);
      onTriggerInfo();
    } else if (lowerChar === 'x') {
      // When triggering X, do not start swing physics so it plummets cleanly
      onTriggerX();
    }
  };

  return (
    <div
      ref={containerRef}
      className={`paper-scrap paper-scrap-${lowerChar} ${isInteractive && !isCollapsed ? 'interactive-scrap' : ''}`}
      onClick={isInteractive && !isCollapsed ? handleClick : undefined}
      onPointerDown={isInteractive && !isCollapsed ? () => {
        isPointerDownRef.current = true;
      } : undefined}
      onPointerUp={isInteractive && !isCollapsed ? () => {
        isPointerDownRef.current = false;
      } : undefined}
      onPointerCancel={isInteractive && !isCollapsed ? () => {
        isPointerDownRef.current = false;
      } : undefined}
      style={{
        position: 'absolute',
        top: '0',
        left: '0',
        width: '100%',
        height: '100%',
        zIndex: 5,
        cursor: isInteractive && !isCollapsed ? 'pointer' : 'default',
        transformOrigin: '50% 12px',
        transform: isCollapsed ? undefined : `rotate(${pendulum.angle.toFixed(2)}deg)`,
        filter: 'drop-shadow(2px 5px 8px rgba(0, 0, 0, 0.45))',
        ['--scrap-angle' as string]: `${pendulum.angle.toFixed(2)}deg`,
        userSelect: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: isCollapsed ? undefined : 'none'
      }}
    >
      {paperImgSrc ? (
        <img
          src={paperImgSrc}
          alt={`letter paper ${char}`}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            display: 'block',
            pointerEvents: 'none'
          }}
          onError={() => {
            if (paperImgSrc.startsWith('/')) {
              setPaperImgSrc(paperImgSrc.slice(1));
            } else {
              setPaperImgSrc(null);
            }
          }}
        />
      ) : (
        <div
          style={{
            width: '100%',
            height: '100%',
            backgroundImage: `url("${bgImage}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            boxShadow: 'inset 0 0 8px rgba(0, 0, 0, 0.15)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            paddingTop: '6px'
          }}
          onError={() => {
            if (bgImage !== SCRAP_FALLBACK) setBgImage(SCRAP_FALLBACK);
          }}
        >
          <div
            className="pin-hole"
            style={{
              position: 'absolute',
              top: '6px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '3.5px',
              height: '3.5px',
              borderRadius: '50%',
              backgroundColor: '#1a1a1a',
              boxShadow: '0 0.5px 1px rgba(255, 255, 255, 0.4)',
              pointerEvents: 'none'
            }}
          />
          <span
            className="paper-char"
            style={{
              fontFamily: "Times, 'Times New Roman', serif",
              fontSize: 'clamp(1.4rem, 2.8vw, 2.2rem)',
              fontWeight: 700,
              color: '#111111',
              lineHeight: 1,
              pointerEvents: 'none'
            }}
          >
            {char}
          </span>
        </div>
      )}
    </div>
  );
};

export default PaperScrap;
